import { useCallback, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useCurrentAuthenticatedUser } from '#src/app/shared/hooks/session';
import type { MutationState } from '#src/app/shared/utils/async';

import type { ReopenPetitionErrorCodes } from '../constants';
import { REOPEN_PETITION_ERROR_MAP, reopenPetitionErrors } from '../constants';

import { reopenPetition } from './api';

type ReopenPetitionData = { status: 'ok' };
type RequestState = MutationState<{ data: ReopenPetitionData }, { error: ReopenPetitionErrorCodes }>;

type Callbacks = {
	onSuccess?: (data: ReopenPetitionData) => void;
	onError?: (error: ReopenPetitionErrorCodes) => void;
};
type HookOptions = Callbacks;
type MutationOptions = Callbacks;
type UseReopenPetitionReturn = RequestState & {
	mutate: (options?: MutationOptions) => Promise<Extract<RequestState, { status: 'loaded' } | { status: 'error' }>>;
	reset: () => void;
};

export function useReopenPetition(id: string, { onSuccess, onError }: HookOptions = {}): UseReopenPetitionReturn {
	const utilityContext = useUtilityContext();
	const user = useCurrentAuthenticatedUser();
	const [state, setState] = useState<RequestState>({ status: 'idle' });

	const handleReopenPetition = useCallback(
		async ({ onSuccess: onSuccessCallback, onError: onErrorCallback }: MutationOptions = {}) => {
			setState({ status: 'loading' });

			try {
				const result = await reopenPetition({ id, user }, utilityContext);
				const successStatus = { status: 'loaded' as const, data: result };
				setState(successStatus);
				onSuccess?.(result);
				onSuccessCallback?.(result);
				return successStatus;
			} catch (e) {
				const errorMessage = parseError(e);
				const errorStatus = { status: 'error' as const, error: errorMessage };
				setState(errorStatus);
				onError?.(errorMessage);
				onErrorCallback?.(errorMessage);
				return errorStatus;
			}
		},
		[id, onError, onSuccess, user, utilityContext],
	);

	const handleReset = useCallback(() => {
		setState({ status: 'idle' });
	}, []);

	return { mutate: handleReopenPetition, reset: handleReset, ...state };
}

function parseError(error: unknown) {
	const err = error instanceof Error ? error.message : undefined;
	return reopenPetitionErrors.find((val) => val === err) ?? REOPEN_PETITION_ERROR_MAP.UNKNOWN_ERROR;
}
